import React, { useState, useEffect } from 'react';
import { useAppContext } from '../AppContext';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import LoadingSpinner from '../components/LoadingSpinner';
import { motion } from 'framer-motion';
import { CheckCircle, Circle } from 'lucide-react';
import Confetti from '../components/Showdown/Confetti';
import '../components/Showdown/Confetti.css';

// Static quest definitions for V1
const dailyQuests = [
    { id: 'win1', title: 'Win 1 Challenge', description: 'Prevail in a one-on-one showdown.', goal: 1, reward: 25 },
    { id: 'play3', title: 'Play 3 Matches', description: 'Compete in three challenges, win or lose.', goal: 3, reward: 50 },
    { id: 'cheer5', title: 'Cheer 5 Times', description: 'Show your support for players in spectator mode.', goal: 5, reward: 10 },
];

const ProgressBar = ({ progress, goal }) => {
    const percentage = Math.min((progress / goal) * 100, 100);
    return (
        <div className="w-full bg-gray-700 rounded-full h-2.5 mt-1">
            <motion.div
                className="bg-cyan-400 h-2.5 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
            />
        </div>
    );
};

const QuestBoard = () => {
    const { db, user } = useAppContext();
    const [questProgress, setQuestProgress] = useState({});
    const [loading, setLoading] = useState(true);
    const [showConfettiFor, setShowConfettiFor] = useState(null);

    useEffect(() => {
        if (!db || !user) {
            setLoading(false);
            return;
        }

        const fetchQuestProgress = async () => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const startOfDay = Timestamp.fromDate(today);

            const challengesRef = collection(db, 'artifacts/whispers-of-harmony/public/data/challenges');
            const q = query(challengesRef, 
                where('participants', 'array-contains', user.uid),
                where('createdAt', '>=', startOfDay)
            );
            const challengesSnapshot = await getDocs(q);
            
            let wins = 0;
            let matchesPlayed = 0;
            challengesSnapshot.forEach(doc => {
                const match = doc.data();
                if (match.status === 'completed') {
                    matchesPlayed++;
                    if (match.winnerId === user.uid) {
                        wins++;
                    }
                }
            });

            setQuestProgress({
                win1: wins,
                play3: matchesPlayed,
                cheer5: 0, // Mocked for now
            });
            setLoading(false);
        };

        fetchQuestProgress();
    }, [db, user]);

    const handleClaimReward = (questId) => {
        setShowConfettiFor(questId);
        setTimeout(() => setShowConfettiFor(null), 2000);
        // V1: UI only. In a real implementation, this would call a cloud function.
        // alert(`Reward for ${questId} claimed! (Note: This is a UI demonstration)`);
    };

    if (loading) return <LoadingSpinner message="Loading Quests..." />;

    return (
        <div className="w-full h-full bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl text-white p-4 md:p-8 flex flex-col overflow-hidden">
            <div className="text-center mb-10 mt-4">
                <h1 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 font-orbitron drop-shadow-sm">Daily Quests</h1>
                <p className="text-gray-300 mt-2 font-light">Complete challenges for daily rewards. Resets in 24 hours.</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-4 w-full overflow-y-auto custom-scrollbar pb-20">
                {dailyQuests.map(quest => {
                    const progress = questProgress[quest.id] || 0;
                    const isComplete = progress >= quest.goal;
                    return (
                        <div key={quest.id} className={`bg-gray-800/40 backdrop-blur-md border border-white/10 rounded-xl p-5 flex items-center justify-between transition-all hover:bg-gray-800/60 ${isComplete ? 'border-green-500/50 shadow-[0_0_10px_rgba(34,197,94,0.2)]' : ''}`}>
                            <div className="flex items-center flex-grow">
                                <div className="mr-5">
                                    {isComplete ? <CheckCircle className="text-green-400" size={32} /> : <Circle className="text-gray-600" size={32} />}
                                </div>
                                <div className="flex-grow">
                                    <h2 className="text-lg font-bold text-white font-orbitron">{quest.title}</h2>
                                    <p className="text-sm text-gray-400">{quest.description}</p>
                                    <ProgressBar progress={progress} goal={quest.goal} />
                                </div>
                            </div>
                            <div className="text-right ml-4 flex-shrink-0">
                                <p className="text-sm font-bold text-white mb-2">{progress} / {quest.goal}</p>
                                <button 
                                    onClick={() => handleClaimReward(quest.id)}
                                    disabled={!isComplete}
                                    className="relative px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 rounded-lg font-bold text-xs disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed disabled:text-gray-500 transition-all shadow-lg shadow-indigo-500/20 btn-interactive border border-white/10"
                                >
                                    {showConfettiFor === quest.id && <Confetti />}
                                    Claim {quest.reward} Echoes
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default QuestBoard;
